import { useState, useEffect, useRef } from 'react';
import {
  Clock, Coffee, UtensilsCrossed, CheckCircle,
  PlayCircle, StopCircle, Calendar, AlertCircle, Bell
} from 'lucide-react';

// Importa las funciones reales de tu API
import {
  getAsesorEstados,
  getAsesorStatus,
  transitionEstadoAsesor,
  getHorarioActualAsesor,
  getJornadaActual,
  marcarEntrada,
  marcarSalida,
  getHistorialAsesor,
  getAsesorJornada,
} from '../lib/workforce';

import { getUserFromToken } from '../lib/api';

import { notify, toastLite, notifySuccess, notifyError, notifyWarn, notifyInfo,
  notifyPromise, notifyAction, dismiss } from '../lib/notify.jsx';

// Helper para obtener usuario actual (prioriza token JWT)
const getUser = () => {
  // Intentar obtener usuario del token JWT actual
  const userFromToken = getUserFromToken();
  if (userFromToken && userFromToken.id) {
    return userFromToken;
  }

  // Fallback al localStorage (para compatibilidad)
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

export default function Marcador() {
  const [user, setUser] = useState(getUser());
  const [contador, setContador] = useState(null);

  // Sincronizar usuario cuando cambie el token
  useEffect(() => {
    const handleStorageChange = () => {
      setUser(getUser());
    };

    window.addEventListener('storage', handleStorageChange);

    const interval = setInterval(() => {
      setUser(getUser());
    }, 30000); // Cada 30 segundos

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // ⏱️ Configuración de inactividad
  const INACTIVIDAD_WARNING_MIN = 55;
  const INACTIVIDAD_MAX_MIN = 60;
  const CIERRE_INACTIVIDAD_CODE = 4001;
  const SYNC_INTERVAL_MS = 30000; // 30 segundos
  const WS_PING_INTERVAL_MS = 25000; // 25 segundos
  
  // 🆕 Configuración de finalización automática
  const MARGEN_FINALIZACION_MIN = 15; // 15 minutos después de la hora de salida
  const CHECK_AUTO_FINISH_MS = 30000; // Verificar cada 30 segundos

  // Obtener el ID del asesor con todas las variantes posibles
  const asesorId =
    user?.id_asesor ??
    user?.asesor_id ??
    user?.idAsesor ??
    user?.external_id ??
    user?.id;

  // 🧠 Estado de datos
  const [estadosDisponibles, setEstadosDisponibles] = useState([]);
  const [cargandoEstados, setCargandoEstados] = useState(true);
  const [errorEstados, setErrorEstados] = useState(null);

  // Reloj / jornada
  const [estadoActual, setEstadoActual] = useState('desconectado');
  const estadoActualRef = useRef('desconectado');

  // 🆕 Estado para contador de límite
  const [tiempoRestanteLimite, setTiempoRestanteLimite] = useState(null);
  const [mostrarAlertaLimite, setMostrarAlertaLimite] = useState(false);

  const [jornadaData, setJornadaData] = useState(null);
  const [estadosTiempo, setEstadosTiempo] = useState({});

  // Mantener el ref sincronizado con el estado
  useEffect(() => {
    estadoActualRef.current = estadoActual;
  }, [estadoActual]);

  const [tiempoActual, setTiempoActual] = useState(0);
  const [horaInicio, setHoraInicio] = useState(null);
  const [registroHoy, setRegistroHoy] = useState({
    id: null,
    usuarioId: asesorId,
    fecha: new Date().toISOString().split('T')[0],
    horaEntrada: null,
    horaSalida: null,
    estado: 'desconectado',
    breaks: [],
    almuerzo: null,
    tiempoTotal: 0,
    tiempoReponer: 0,
  });

  // WebSocket
  const [ws, setWs] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  // ⏱️ Inactividad
  const ultimaActividadRef = useRef(Date.now());
  const [mostrarModalInactividad, setMostrarModalInactividad] = useState(false);
  const [requiereReconexion, setRequiereReconexion] = useState(false);
  const requiereReconexionRef = useRef(false);
  const mostrarModalInactividadRef = useRef(false);

  // Límite y excedente
  const [limiteActualSeg, setLimiteActualSeg] = useState(null);
  const [excedenteActual, setExcedenteActual] = useState(0);
  const [alertado, setAlertado] = useState(false);

  // Beep
  const beepRef = useRef(null);
  const intervalRef = useRef(null);
  const verificandoJornadaRef = useRef(false);
  const syncIntervalRef = useRef(null);
  const pingIntervalRef = useRef(null);

  // Horario y permisos
  const [horarioHoy, setHorarioHoy] = useState(null);
  const [puedeIniciar, setPuedeIniciar] = useState(false);
  const [puedeFinalizar, setPuedeFinalizar] = useState(false);
  const [mensajeHorario, setMensajeHorario] = useState('');
  const [jornadaCerrada, setJornadaCerrada] = useState(false);
  const [fueraDeHorario, setFueraDeHorario] = useState(false);
  const [historialHoy, setHistorialHoy] = useState([]);
  
  // 🔥 Estado y ref para tiempo en estado actual
  const [inicioEstadoActual, setInicioEstadoActual] = useState(null);
  const inicioEstadoActualRef = useRef(null);
  
  // 🆕 Estados para finalización automática
  const [proximaFinalizacionAuto, setProximaFinalizacionAuto] = useState(null);
  const [alertaFinalizacionMostrada, setAlertaFinalizacionMostrada] = useState(false);
  const [tiempoRestanteFinalizacion, setTiempoRestanteFinalizacion] = useState(null);
  const autoFinishCheckRef = useRef(null);
  const finalizacionAutoPendienteRef = useRef(false);
  const [limiteExcedido, setLimiteExcedido] = useState(false);

  const [tiempoRestanteEnVivo, setTiempoRestanteEnVivo] = useState(null);
  

  const isoColombia = () => {
    return new Date()
      .toLocaleString("sv-SE", { timeZone: "America/Bogota" })
      .replace(" ", "T"); 
  };

  // Sincronizar el ref con el estado
  useEffect(() => {
    inicioEstadoActualRef.current = inicioEstadoActual;
  }, [inicioEstadoActual]);

  // Mantener los refs sincronizados con el estado
  useEffect(() => {
    requiereReconexionRef.current = requiereReconexion;
  }, [requiereReconexion]);

  useEffect(() => {
    mostrarModalInactividadRef.current = mostrarModalInactividad;
  }, [mostrarModalInactividad]);

  // 🆕 Función para calcular la hora de finalización automática
  const calcularHoraFinalizacionAuto = (horaSalida) => {
    if (!horaSalida) return null;
    
    const [horas, minutos] = horaSalida.split(':').map(Number);
    const horaFinalizacion = new Date();
    horaFinalizacion.setHours(horas, minutos + MARGEN_FINALIZACION_MIN, 0, 0);
    
    return horaFinalizacion;
  };

  // 🆕 Función de finalización automática
  const finalizarJornadaAutomatica = async () => {
    if (finalizacionAutoPendienteRef.current) {
      console.log('⏭️ Finalización automática ya en proceso, omitiendo...');
      return;
    }

    console.log('🤖 Iniciando finalización automática de jornada...');
    finalizacionAutoPendienteRef.current = true;

    try {
      // Llamar a la función de marcar salida
      await handleMarcarSalida(true); // true = es automático
      
      // Notificación al usuario
      notifyWarn(
        'Tu jornada fue finalizada automáticamente porque superaste el tiempo de finalización permitido.',
        { duration: 10000 }
      );
      
    } catch (error) {
      console.error('Error en finalización automática:', error);
      notifyError('Error al finalizar jornada automáticamente: ' + error.message);
    } finally {
      finalizacionAutoPendienteRef.current = false;
    }
  };

  // 🆕 Monitoreo de finalización automática
  useEffect(() => {
    // Limpiar cualquier verificación previa
    if (autoFinishCheckRef.current) {
      clearInterval(autoFinishCheckRef.current);
      autoFinishCheckRef.current = null;
    }

    // Solo activar si hay jornada activa y no está cerrada
    const jornadaActiva = registroHoy.horaEntrada && !registroHoy.horaSalida && !jornadaCerrada;
    
    if (!jornadaActiva || !horarioHoy?.hora_salida) {
      setProximaFinalizacionAuto(null);
      setTiempoRestanteFinalizacion(null);
      return;
    }

    const horaFinalizacion = calcularHoraFinalizacionAuto(horarioHoy.hora_salida);
    setProximaFinalizacionAuto(horaFinalizacion);

    console.log('⏰ Sistema de finalización automática activado');
    console.log('📅 Hora de salida programada:', horarioHoy.hora_salida);
    console.log('🔔 Finalización automática a las:', horaFinalizacion.toLocaleTimeString());

    // Verificación periódica
    autoFinishCheckRef.current = setInterval(() => {
      const ahora = new Date();
      
      // Calcular tiempo restante
      const diffMs = horaFinalizacion.getTime() - ahora.getTime();
      const minutosRestantes = Math.floor(diffMs / 60000);
      setTiempoRestanteFinalizacion(minutosRestantes);

      //notifyWarn(`Tiempo restante para finalización automática: ${minutosRestantes} minutos`);


      // Alerta 5 minutos antes
      if (minutosRestantes <= 5 && minutosRestantes > 0 && !alertaFinalizacionMostrada) {
        console.log('⚠️ Mostrando alerta de finalización próxima');
        notifyWarn(
          `Tu jornada se finalizará automáticamente en ${minutosRestantes} minutos. Por favor, finaliza manualmente si has terminado.`,
          { duration: 8000 }
        );
        playBeep();
        setAlertaFinalizacionMostrada(true);
      }

      // Ejecutar finalización automática
      if (ahora >= horaFinalizacion && !jornadaCerrada) {
        console.log('🚨 Hora de finalización automática alcanzada');
        playBeep();
        finalizarJornadaAutomatica();
      }
    }, CHECK_AUTO_FINISH_MS);

    return () => {
      if (autoFinishCheckRef.current) {
        clearInterval(autoFinishCheckRef.current);
        autoFinishCheckRef.current = null;
      }
    };
  }, [registroHoy.horaEntrada, registroHoy.horaSalida, jornadaCerrada, horarioHoy, alertaFinalizacionMostrada]);

  // Resetear alerta cuando cambia el horario o se cierra la jornada
  useEffect(() => {
    if (jornadaCerrada || !registroHoy.horaEntrada) {
      setAlertaFinalizacionMostrada(false);
    }
  }, [horarioHoy?.hora_salida, jornadaCerrada, registroHoy.horaEntrada]);

  // 🔄 Sincronización periódica de estado (cada 30 segundos)
  // 🔄 Sincronización periódica de estado (cada 30 segundos)
  // 🔄 Sincronización periódica de estado (cada 30 segundos)
  useEffect(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
    }

    syncIntervalRef.current = setInterval(() => {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        //console.log('[Sync] WebSocket no disponible, omitiendo sync');
        return;
      }

      const estado = (estadoActualRef.current || '').toLowerCase();
      const inicio = inicioEstadoActualRef.current;
      
      // 🔥 NO enviar si no hay inicio_estado válido o está desconectado o en jornada_activa
      if (!inicio || estado === 'desconectado' || estado === 'cierrejornada' || estado === 'jornada_activa') {
        //console.log('[Sync] Omitiendo - sin inicio_estado válido o desconectado');
        return;
      }
      
      // 🔥 Calcular tiempo actual
      const tiempoEnEstado = Math.floor((Date.now() - new Date(inicio).getTime()) / 1000);

      console.log('[Sync] Enviando:', { estado, inicio, tiempo: tiempoEnEstado });

      try {
        ws.send(JSON.stringify({
          type: 'estado_cambio',
          userId: asesorId,
          nombre: user.nombre,
          cargo: user.cargo,
          area: user.area,
          estado,
          nuevo_estado: estado,
          estado_slug: estado,
          estado_actual: estado,
          estado_actual_slug: estado,
          timestamp: new Date().toISOString(),
          origen: 'sync_interval',
          tiempo_en_estado_segundos: tiempoEnEstado,
          tiempo_en_estado: tiempoEnEstado,
          inicio_estado: inicio,
          inicio: inicio,
          horaEntrada: registroHoy.horaEntrada,
          horaSalida: registroHoy.horaSalida,
          tiempoActual,
          jornadaActiva: registroHoy.horaEntrada && !registroHoy.horaSalida,
        }));

        console.log('[Sync] ✅ Estado enviado:', estado, '| Tiempo:', tiempoEnEstado, 's');
      } catch (error) {
        console.error('[Sync] Error enviando estado:', error);
        // Si falla el envío, limpiar el intervalo para evitar spam
        if (syncIntervalRef.current) {
          clearInterval(syncIntervalRef.current);
          syncIntervalRef.current = null;
        }
      }
    }, SYNC_INTERVAL_MS);

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
    };
  }, [ws, asesorId, user.nombre, user.cargo, user.area, registroHoy, tiempoActual]);

  const playBeep = () => {
    try {
      const audio = beepRef.current;
      if (!audio) return;

      audio.currentTime = 0;
      audio.play().catch((err) => {
        console.log('No se pudo reproducir el beep:', err);
      });
    } catch (e) {
      console.log('Error al reproducir beep:', e);
    }
  };

  useEffect(() => {
    const uri = 'data:audio/wav;base64,UklGRnQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABYBWFlaAAABAAACAAAAPwAAAP8AAAABAQAA//8AAP//AACAgIAAAP8AAP//AAAAAAA=';
    const a = new Audio(uri);
    a.volume = 0.6;
    beepRef.current = a;
  }, []);

  // 📨 Manejar mensajes del WebSocket
  useEffect(() => {
    if (!ws) return;

    ws.onmessage = async (e) => {
      let msg;
      try {
        msg = JSON.parse(e.data);
      } catch {
        return;
      }

      console.log('[WS] Mensaje recibido:', msg.type, msg);

      switch (msg.type) {
        case 'pong':
          console.log('[WS] Pong recibido');
          break;
          
        case 'estado_actualizado':
          if (msg.userId === asesorId) {
            await cargarEstadoActualBackend();
            notifyInfo('Tu estado fue actualizado automáticamente');
          }
          break;
          
        case 'jornada_actualizada':
          if (msg.userId === asesorId) {
            await verificarJornadaActual();
            notifyInfo('Tu jornada cambió');
          }
          break;
          
        case 'horario_actualizado':
          if (msg.userId === asesorId) {
            await cargarHorarioHoy();
            notifyInfo('Tu horario cambió');
          }
          break;
          
        case 'historial_actualizado':
          if (msg.userId === asesorId) {
            await cargarHistorialHoy();
            notifyInfo('Se actualizó tu historial de estados');
          }
          break;

        case 'estado_cambio':
          if (msg.userId !== asesorId) return;
          break;

        default:
          break;
      }
    };

    return () => {
      ws.onmessage = null;
    };
  }, [ws, asesorId]);

  // 🚀 Inicialización
  useEffect(() => {
    const init = async () => {
      console.log('🚀 Iniciando carga del marcador...');
      await cargarEstadosDelAsesor();
      await cargarHorarioHoy();
      console.log('📋 Verificando jornada actual...');
      await verificarJornadaActual();

      console.log('⏳ Esperando 1.5s antes de cargar estado del backend...');
      setTimeout(async () => {
        console.log('📊 Cargando estado actual del backend...');
        await cargarEstadoActualBackend();
        console.log('✅ Carga de estado completada');
      }, 1500);

      await cargarHistorialHoy();
      console.log('✅ Carga inicial completada');
    };
    init();

    const websocket = conectarWebSocket();

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (autoFinishCheckRef.current) {
        clearInterval(autoFinishCheckRef.current);
        autoFinishCheckRef.current = null;
      }
    };
  }, []);

  // 🔄 Actualización periódica de horario y jornada
  useEffect(() => {
    const interval = setInterval(async () => {
      if (!jornadaCerrada && estadoActual === 'desconectado' && !registroHoy.horaEntrada) {
        await cargarHorarioHoy();
        await verificarJornadaActual();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [asesorId, jornadaCerrada, estadoActual, registroHoy.horaEntrada]);

  // ⏱️ Cronómetro principal con control de límite
  // ⏱️ Cronómetro principal con control de límite y contador regresivo
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const puedeActivarCronometro = 
      estadoActual !== 'desconectado' && 
      horaInicio &&
      registroHoy.horaEntrada;

    if (puedeActivarCronometro) {
      intervalRef.current = setInterval(() => {
        const ahora = new Date();
        const inicio = new Date(horaInicio);
        const diff = Math.floor((ahora - inicio) / 1000);
        setTiempoActual(diff);

        if (limiteActualSeg != null) {
          const over = Math.max(0, diff - limiteActualSeg);
          setExcedenteActual(over);

          // 🆕 Calcular tiempo restante
          const restante = Math.max(0, limiteActualSeg - diff);
          setTiempoRestanteLimite(restante);

          // 🆕 Mostrar alerta cuando quedan 10 segundos o menos
          if (restante <= 10 && restante > 0 && !mostrarAlertaLimite) {
            setMostrarAlertaLimite(true);
            playBeep();
          }

          // 🆕 Ocultar alerta cuando se pasa el límite
          if (restante === 0 && mostrarAlertaLimite) {
            setMostrarAlertaLimite(false);
          }

          if (!alertado && over > 0) {
            try {
              if (beepRef.current) {
                beepRef.current.currentTime = 0;
                beepRef.current.play().catch(err => console.log('Audio error:', err));
              }
            } catch (err) {
              console.log('Error playing audio:', err);
            }
            setAlertado(true);
            enviarEstadoWebSocket('limite_superado', {
              estado: estadoActual,
              limite_segundos: limiteActualSeg,
            });
          }
        } else {
          setTiempoRestanteLimite(null);
          setMostrarAlertaLimite(false);
        }
      }, 1000);
    } else {
      setTiempoActual(0);
      setTiempoRestanteLimite(null);
      setMostrarAlertaLimite(false);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [estadoActual, horaInicio, limiteActualSeg, alertado, registroHoy.horaEntrada, mostrarAlertaLimite]);


  const calcularTiemposPorEstado = (items) => {
    const tiempos = {};
    
    items.forEach(item => {
      const estado = item.estado;
      const segundos = item.segundos || 0;
      
      if (!tiempos[estado]) {
        tiempos[estado] = {
          total_segundos: 0,
          total_minutos: 0,
          color: item.color,
          nombre: item.nombre
        };
      }
      
      tiempos[estado].total_segundos += segundos;
      tiempos[estado].total_minutos = Math.floor(tiempos[estado].total_segundos / 60);
    });
    
    return tiempos;
  };

  useEffect(() => {
    const fetchJornadaData = async () => {
      if (!asesorId || estadoActual === 'desconectado') return;
      
      try {
        const response = await getAsesorJornada(asesorId);
        setJornadaData(response);
        
        if (response?.items) {
          const tiempos = calcularTiemposPorEstado(response.items);
          setEstadosTiempo(tiempos);
        }
      } catch (error) {
        console.error('Error al cargar jornada:', error);
      }
    };

    fetchJornadaData();
    
    // Actualizar cada 30 segundos
    const interval = setInterval(fetchJornadaData, 30000);
    
    return () => clearInterval(interval);
  }, [asesorId, estadoActual]);


  const getTiempoRestante = (estado) => {
    const tiempoUsadoHoy = estadosTiempo[estado]?.total_segundos || 0;
    const limite = limiteActualSeg || 0;
    
    // Restar el tiempo actual porque ya está corriendo
    return limite - tiempoUsadoHoy - tiempoActual;
  };
  // Recalcular límite cuando cambia el estado



  // Recalcular límite cuando cambia el estado
  useEffect(() => {
    const e = estadosDisponibles.find(x => (x.codigo || x.slug) === estadoActual);
    const min = e?.limite_minutos ?? e?.limite_minutos_default ?? null;
    const lim = (typeof min === 'number' && !isNaN(min)) ? min * 60 : null;
    setLimiteActualSeg(lim);
    setExcedenteActual(0);
    setAlertado(false);
    setMostrarAlertaLimite(false); // 🆕 Resetear alerta
    setTiempoRestanteLimite(null); // 🆕 Resetear tiempo restante
  }, [estadoActual, estadosDisponibles]);

  useEffect(() => {
    const e = estadosDisponibles.find(x => (x.codigo || x.slug) === estadoActual);
    const min = e?.limite_minutos ?? e?.limite_minutos_default ?? null;
    const lim = (typeof min === 'number' && !isNaN(min)) ? min * 60 : null;
    setLimiteActualSeg(lim);
    setExcedenteActual(0);
    setAlertado(false);
  }, [estadoActual, estadosDisponibles]);

  // Funciones API
  const cargarEstadosDelAsesor = async () => {
    try {
      setCargandoEstados(true);
      const estados = await getAsesorEstados(asesorId);
      setEstadosDisponibles(estados || []);
    } catch (err) {
      console.error('Error cargando estados:', err);
    } finally {
      setCargandoEstados(false);
    }
  };

  const cargarEstadoActualBackend = async () => {
    try {
      const res = await getAsesorStatus(asesorId);

      console.log('📊 [cargarEstadoActualBackend] Respuesta del backend:', res);

      // Manejar el caso donde estado es null
      let codigo = null;
      if (res?.estado && typeof res.estado === 'string') {
        codigo = res.estado.toLowerCase();
      } else if (res?.codigo && typeof res.codigo === 'string') {
        codigo = res.codigo.toLowerCase();
      }

      const inicioIso = res?.inicio;

      console.log("inicioIso", inicioIso);
      console.log("codigo", codigo);

      if (inicioIso) {
        const fechaInicio = new Date(inicioIso).toISOString().split('T')[0];
        const hoy = new Date().toLocaleString('sv-SE', { timeZone: 'America/Bogota' }).split(' ')[0];

        if (fechaInicio !== hoy) {
          console.log('🔴 Estado del backend es de otro día, ignorando');
          return;
        }
      }

      // Si hay un estado activo en el backend, restaurarlo
      if (codigo && codigo !== 'desconectado' && codigo !== 'cierrejornada' && codigo !== 'jornada_activa' && codigo !== 'null') {
        console.log('✅ Restaurando estado del backend:', codigo);

        if (inicioIso) {
          const diff = Math.floor((new Date() - new Date(inicioIso)) / 1000);

          setEstadoActual(codigo);
          setInicioEstadoActual(inicioIso);
          setHoraInicio(inicioIso);
          setTiempoActual(diff);
          setPuedeFinalizar(true);
          setPuedeIniciar(false);
          setJornadaCerrada(false);

          setTimeout(() => {
            enviarEstadoWebSocket(codigo, {
              inicio_estado_override: inicioIso,
              restaurando: true
            });
          }, 500);

          console.log(`✅ Estado restaurado: ${codigo}, tiempo: ${diff}s`);
        }
      } else {
        console.log('🔴 No hay estado activo para restaurar del backend. Código:', codigo);
      }
    } catch (err) {
      console.warn('No se pudo obtener el estado actual del backend:', err);
    }
  };

  const cargarHorarioHoy = async () => {
    try {
      const data = await getHorarioActualAsesor(asesorId);
      if (!data?.hora_entrada || !data?.hora_salida) {
        setPuedeIniciar(false);
        setPuedeFinalizar(false);
        setMensajeHorario('No hay horario asignado para hoy');
        return;
      }

      setHorarioHoy(data);
      const ahora = new Date();
      const [hEntrada, mEntrada] = data.hora_entrada.split(':').map(Number);
      const [hSalida, mSalida] = data.hora_salida.split(':').map(Number);

      const horaEntrada = new Date();
      horaEntrada.setHours(hEntrada, mEntrada, 0, 0);
      const horaSalida = new Date();
      horaSalida.setHours(hSalida, mSalida, 0, 0);

      const margenEntrada = 30 * 60 * 1000;
      const margenSalida = 2 * 60 * 60 * 1000;
      const ventanaInicio = new Date(horaEntrada.getTime() - margenEntrada);
      const ventanaFin = new Date(horaSalida.getTime() + margenSalida);

      const puedeIniciarAhora = ahora >= ventanaInicio && ahora <= ventanaFin;
      const puedeFinalizarAhora = ahora >= horaSalida;

      setPuedeIniciar(puedeIniciarAhora);
      setPuedeFinalizar(puedeFinalizarAhora);

      if (!puedeIniciarAhora && ahora < ventanaInicio) {
        const minutos = Math.ceil((ventanaInicio - ahora) / 60000);
        setMensajeHorario(`Podrás iniciar en ${minutos} minutos`);
      } else if (ahora > ventanaFin) {
        setMensajeHorario('Fuera de la ventana de marcación para hoy');
      } else if (ahora >= horaSalida) {
        const nombre = (user?.nombre || '').split(' ')[0];
        setMensajeHorario(`${nombre}, ya es hora de finalizar tu jornada.`);
      } else {
        setMensajeHorario('');
      }
    } catch (err) {
      console.error('Error cargando horario:', err);
    }
  };

  useEffect(() => {
    if (!horarioHoy) return;

    const ahora = new Date();
    const [hEntrada, mEntrada] = horarioHoy.hora_entrada.split(':').map(Number);
    const [hSalida, mSalida] = horarioHoy.hora_salida.split(':').map(Number);

    const horaEntrada = new Date();
    horaEntrada.setHours(hEntrada, mEntrada, 0, 0);
    const horaSalida = new Date();
    horaSalida.setHours(hSalida, mSalida, 0, 0);

    const margenInicio = new Date(horaEntrada.getTime() - 30 * 60 * 1000);
    const margenFin = new Date(horaSalida.getTime() + 30 * 60 * 1000);

    setFueraDeHorario(!(ahora >= margenInicio && ahora <= margenFin));
  }, [horarioHoy, tiempoActual]);

  const enviarEstadoWebSocket = (nuevoEstado, datos = {}) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn('[WS] No se puede enviar, conexión no disponible');
      return;
    }

    const estadoSlug = (nuevoEstado || '').toString().toLowerCase();
    const inicio = datos.inicio_estado_override || inicioEstadoActualRef.current;

    console.log('🧪 [DIAGNÓSTICO enviarEstadoWebSocket]', {
      nuevoEstado,
      estadoSlug,
      inicio_del_ref: inicioEstadoActualRef.current,
      inicio_override: datos.inicio_estado_override,
      inicio_final: inicio,
      datos_completos: datos
    });
    
    let tiempoEnEstado = 0;
    if (inicio) {
      const inicioMs = new Date(inicio).getTime();
      const ahoraMs = Date.now();
      tiempoEnEstado = Math.floor((ahoraMs - inicioMs) / 1000);
    }

    try {
      const payload = {
        type: 'estado_cambio',
        userId: asesorId,
        nombre: user.nombre,
        cargo: user.cargo,
        area: user.area,
        estado: estadoSlug,
        nuevo_estado: estadoSlug,
        estado_slug: estadoSlug,
        estado_actual: estadoSlug,
        estado_actual_slug: estadoSlug,
        timestamp: new Date().toISOString(),
        tiempo_en_estado_segundos: tiempoEnEstado,
        tiempo_en_estado: tiempoEnEstado,
        inicio_estado: inicio,
        inicio: inicio,
        ...datos
      };
      
      console.log('[WS] Enviando:', { estado: estadoSlug, inicio, tiempo: tiempoEnEstado });
      ws.send(JSON.stringify(payload));
    } catch (error) {
      console.error('[WS] Error enviando estado:', error);
    }
  };

  useEffect(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (!asesorId) return;
    
    const inicio = inicioEstadoActualRef.current;
    if (!inicio && estadoActual !== 'desconectado') {
      console.log('[Estado] Omitiendo envío - sin inicio_estado válido');
      return;
    }

    enviarEstadoWebSocket(estadoActual, {
      inicio_estado_override: inicio,
      horaEntrada: registroHoy.horaEntrada,
      horaSalida: registroHoy.horaSalida,
      jornadaActiva: registroHoy.horaEntrada && !registroHoy.horaSalida,
    });
  }, [estadoActual, ws, asesorId]);

  const verificarJornadaActual = async () => {
    if (verificandoJornadaRef.current) {
      console.log('⏭️ Ya se está verificando jornada, omitiendo...');
      return;
    }

    try {
      verificandoJornadaRef.current = true;
      console.log('🔍 [verificarJornadaActual] Obteniendo jornada del asesor:', asesorId);
      const jornada = await getJornadaActual(asesorId);
      console.log('📋 [verificarJornadaActual] Jornada recibida:', jornada);

      // Usar la fecha de Colombia para comparar
      const hoyStr = new Date().toLocaleString('sv-SE', { timeZone: 'America/Bogota' }).split(' ')[0];

      if (jornada?.fecha && jornada.fecha !== hoyStr) {
        console.log('📅 Jornada de otro día, ignorando', { jornadaFecha: jornada.fecha, hoy: hoyStr });
        return;
      }

      if (!jornada?.items || jornada.items.length === 0) {
        console.log('✅ No hay jornada registrada hoy');
        return;
      }

      const primerItem = jornada.items[0];
      const ultimoItem = jornada.items[jornada.items.length - 1];
      const jornadaActiva = ultimoItem && !ultimoItem.fin && ultimoItem.estado !== 'desconectado' && ultimoItem.estado !== 'cierrejornada';

      if (jornadaActiva) {
        console.log('🟢 Jornada activa detectada');
        console.log('Items de jornada:', jornada.items);
        console.log('Primer item:', primerItem);
        console.log('Último item:', ultimoItem);

        const horaEntrada = new Date(primerItem.inicio).toLocaleString('sv-SE', {timeZone: 'America/Bogota'}).replace(' ', 'T');

        setRegistroHoy(prev => ({
          ...prev,
          horaEntrada: horaEntrada,
          horaSalida: null,
        }));

        // Si solo hay un item y es el de ingreso, usar jornada_activa
        // Si hay más items, el último debería tener el estado real
        let estadoActivo;
        if (jornada.items.length === 1 && (primerItem.estado === 'ingreso' || primerItem.estado === 'jornada_activa')) {
          estadoActivo = 'jornada_activa';
          console.log('Solo hay un item de ingreso, usando jornada_activa');
        } else {
          estadoActivo = ultimoItem.estado || 'disponible';
          console.log('Usando estado del último item:', estadoActivo);
        }

        setEstadoActual(estadoActivo);

        if (ultimoItem.inicio && estadoActivo !== 'desconectado') {
          setHoraInicio(ultimoItem.inicio);
          setInicioEstadoActual(ultimoItem.inicio);
          const diff = Math.floor((new Date() - new Date(ultimoItem.inicio)) / 1000);
          setTiempoActual(diff);
        }

        setJornadaCerrada(false);
        setPuedeIniciar(false);
        setPuedeFinalizar(true);

        console.log('Jornada activa configurada:', {
          horaEntrada,
          estadoActivo,
          puedeIniciar: false,
          puedeFinalizar: true,
          itemsCount: jornada.items.length
        });

        return;
      }

      const salida = ultimoItem.fin
        ? new Date(ultimoItem.fin).toLocaleString('sv-SE', {timeZone: 'America/Bogota'}).replace(' ', 'T')
        : null;

      if (salida || ultimoItem.estado === 'desconectado' || ultimoItem.estado === 'cierrejornada') {
        console.log('Jornada finalizada detectada');
        setRegistroHoy(prev => ({
          ...prev,
          horaEntrada: new Date(primerItem.inicio).toLocaleString('sv-SE', {timeZone: 'America/Bogota'}).replace(' ', 'T'),
          horaSalida: salida,
        }));
        setEstadoActual('desconectado');
        setJornadaCerrada(true);
        setPuedeIniciar(false);
        setPuedeFinalizar(false);
      }

    } catch (err) {
      console.warn('Error verificando jornada:', err);
    } finally {
      verificandoJornadaRef.current = false;
    }
  };

  const handleCambiarEstado = async (nuevoEstado) => {
    if (jornadaCerrada) {
      notifyWarn('Tu jornada ya fue finalizada. No puedes cambiar estados.');
      return;
    }
    const ahora = new Date();
    const horaActual = ahora.toLocaleString('sv-SE', {timeZone: 'America/Bogota'}).replace(' ', 'T');

    try {
      if (nuevoEstado === 'iniciar_jornada') {
        const inicioIso = ahora.toISOString();
        setInicioEstadoActual(inicioIso);
        setHoraInicio(inicioIso);
        setTiempoActual(0);
        setEstadoActual(nuevoEstado);

        enviarEstadoWebSocket(nuevoEstado, {
          inicio_estado_override: inicioIso
        });
        console.log('Jornada iniciada - Estados ahora disponibles (solo frontend)');
        return;
      }

      // Si estamos en jornada_activa y cambiamos a cualquier estado válido
      if (estadoActual === 'jornada_activa' && nuevoEstado !== 'finalizar_jornada' && nuevoEstado !== 'salida') {
        console.log(`Transición desde jornada_activa: ${nuevoEstado}`);

        await transitionEstadoAsesor(asesorId, { estado: nuevoEstado });

        const inicioIso = ahora.toISOString();
        setInicioEstadoActual(inicioIso);
        setHoraInicio(inicioIso);
        setTiempoActual(0);
        setEstadoActual(nuevoEstado);
        enviarEstadoWebSocket(nuevoEstado, {
          inicio_estado_override: inicioIso
        });
        return;
      }

      if (nuevoEstado === 'finalizar_jornada' || nuevoEstado === 'salida') {
        if (excedenteActual > 0) {
          setRegistroHoy(prev => ({
            ...prev,
            tiempoReponer: (prev?.tiempoReponer || 0) + excedenteActual,
          }));
        }
        
        setRegistroHoy(prev => ({ ...prev, horaSalida: horaActual, estado: 'desconectado' }));
        setHoraInicio(null);
        setTiempoActual(0);
        setEstadoActual('desconectado');
        setInicioEstadoActual(null);
        
        const inicioIso = ahora.toISOString();
        enviarEstadoWebSocket('desconectado', {
          inicio_estado_override: inicioIso
        });

        if (estadoActual !== 'jornada_activa' && estadoActual !== 'desconectado') {
          try {
            await transitionEstadoAsesor(asesorId, { estado: 'desconectado' });
          } catch (err) {
            console.warn('No se pudo notificar desconexión al backend:', err);
          }
        }
        
        console.log('Jornada finalizada');
        return;
      }

      console.log(`Transición: ${estadoActual} → ${nuevoEstado}`);

      await transitionEstadoAsesor(asesorId, { estado: nuevoEstado });

      if (excedenteActual > 0 && estadoActual !== 'desconectado' && estadoActual !== 'jornada_activa') {
        setRegistroHoy(prev => ({
          ...prev,
          tiempoReponer: (prev?.tiempoReponer || 0) + excedenteActual,
        }));
      }

 

      if (nuevoEstado === 'disponible') {
        if (estadoActual === 'break') {
          const breakActual = registroHoy.breaks[registroHoy.breaks.length - 1];
          if (breakActual) breakActual.fin = horaActual;
        } else if (estadoActual === 'almuerzo') {
          setRegistroHoy(prev => ({
            ...prev,
            almuerzo: { ...(prev.almuerzo || {}), fin: horaActual }
          }));
        } else if (estadoActual === 'jornada_activa') {
          // Transición desde jornada_activa a disponible
          await transitionEstadoAsesor(asesorId, { estado: 'disponible' });
        }
        const inicioIso = ahora.toISOString();
        setInicioEstadoActual(inicioIso);
        setHoraInicio(inicioIso);
        setTiempoActual(0);
        setEstadoActual('disponible');
        enviarEstadoWebSocket('disponible', {
          inicio_estado_override: inicioIso
        });
        return;
      }

      if (nuevoEstado === 'break') {
        if (estadoActual === 'jornada_activa') {
          await transitionEstadoAsesor(asesorId, { estado: 'break' });
        }
        setRegistroHoy(prev => ({
          ...prev,
          breaks: [...prev.breaks, { inicio: horaActual, fin: null }],
        }));
        const inicioIso = ahora.toISOString();
        setInicioEstadoActual(inicioIso);
        setHoraInicio(inicioIso);
        setTiempoActual(0);
        setEstadoActual('break');
        enviarEstadoWebSocket('break', {
          inicio_estado_override: inicioIso
        });
        return;
      }

      if (nuevoEstado === 'almuerzo') {
        if (estadoActual === 'jornada_activa') {
          await transitionEstadoAsesor(asesorId, { estado: 'almuerzo' });
        }
        setRegistroHoy(prev => ({
          ...prev,
          almuerzo: { inicio: horaActual, fin: null },
        }));
        const inicioIso = ahora.toISOString();
        setInicioEstadoActual(inicioIso);
        setHoraInicio(inicioIso);
        setTiempoActual(0);
        setEstadoActual('almuerzo');
        enviarEstadoWebSocket('almuerzo', {
          inicio_estado_override: inicioIso
        });
        return;
      }

      // Caso genérico para otros estados
      const inicioIso = ahora.toISOString();
      setInicioEstadoActual(inicioIso);
      setHoraInicio(inicioIso);
      setTiempoActual(0);
      setEstadoActual(nuevoEstado);
      enviarEstadoWebSocket(nuevoEstado, {
        inicio_estado_override: inicioIso
      });

    } catch (err) {
      notifyError(`No se pudo cambiar al estado "${nuevoEstado}". ${err.message || 'Intenta nuevamente.'}`);
    }
  };

  const notificarDesconexionWS = (motivo = 'desconocido') => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    try {
      ws.send(JSON.stringify({
        type: 'estado_cambio',
        userId: asesorId,
        nombre: user.nombre,
        cargo: user.cargo,
        area: user.area,
        estado: 'desconectado',
        estado_slug: 'desconectado',
        motivo,
        timestamp: new Date().toISOString(),
        origen: 'auto_disconnect',
      }));
    } catch (error) {
      console.error('[WS] Error notificando desconexión:', error);
    }
  };

  const formatearTiempo = (segundos) => {
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    const segs = segundos % 60;
    return `${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}:${segs.toString().padStart(2, '0')}`;
  };

  const getEstadoColor = () => {
    const encontrado = estadosDisponibles.find(e => (e.codigo || e.slug) === estadoActual);
    if (encontrado?.color_hex) return encontrado.color_hex;

    switch (estadoActual) {
      case 'jornada_activa': return '#3b82f6';
      case 'disponible': return '#82cc0e';
      case 'break': return '#f59e0b';
      case 'almuerzo': return '#82cc0e';
      default: return '#6b7280';
    }
  };

  const getEstadoTexto = () => {
      const encontrado = estadosDisponibles.find(e => (e.codigo || e.slug) === estadoActual);
      if (encontrado?.nombre) return encontrado.nombre;

      switch (estadoActual) {
        case 'jornada_activa': return 'Jornada Iniciada';
        case 'disponible': return 'Disponible';
        case 'break': return 'En Break';
        case 'almuerzo': return 'En Almuerzo';
        default: return 'Desconectado';
      }
  };

  const labelDe = (codigo) => {
    switch ((codigo || '').toLowerCase()) {
      case 'ingreso': return 'Ingresar';
      case 'salida': return 'Finalizar Jornada';
      case 'disponible': return 'Disponible';
      case 'break': return 'Break';
      case 'almuerzo': return 'Almuerzo';
      case 'reunion': return 'Reunión';
      default: return codigo?.charAt(0).toUpperCase() + codigo?.slice(1);
    }
  };

  const iconoDe = (codigo) => {
    switch ((codigo || '').toLowerCase()) {
      case 'ingreso': return PlayCircle;
      case 'salida': return StopCircle;
      case 'jornada_activa': return Clock;
      case 'disponible': return CheckCircle;
      case 'break': return Coffee;
      case 'almuerzo': return UtensilsCrossed;
      default: return Clock;
    }
  };

  const colorDe = (codigo) => {
    switch ((codigo || '').toLowerCase()) {
      case 'jornada_activa': return '#3b82f6';
      case 'disponible': return '#10b981';
      case 'break': return '#f59e0b';
      case 'almuerzo': return '#3b82f6';
      case 'ingreso': return '#10b981';
      case 'salida': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const handleMarcarEntrada = async () => {
    try {
      await marcarEntrada(asesorId);
      const inicioIso = isoColombia();
      setInicioEstadoActual(inicioIso);
      setRegistroHoy(prev => ({
        ...prev,
        horaEntrada: new Date().toLocaleString('sv-SE', {timeZone: 'America/Bogota'}).replace(' ', 'T'),
      }));
      setEstadoActual('jornada_activa');
      setPuedeIniciar(false);
      setPuedeFinalizar(true);
      notifySuccess('Entrada marcada correctamente');
      await verificarJornadaActual();

      // 🆕 Auto-seleccionar estado "Disponible" después de 1 segundo
      setTimeout(() => {
        handleCambiarEstado('disponible');
      }, 1000);
    } catch (err) {
      notifyError(`Error al marcar entrada: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleMarcarSalida = async (esAutomatico = false) => {
    try {
      const ahora = new Date();
      const horaSalidaActual = ahora.toLocaleString('sv-SE', {timeZone: 'America/Bogota'}).replace(' ', 'T');
      const finISOString = ahora.toISOString();
      const estadoAnterior = estadoActual;

      console.log(esAutomatico ? '🤖 Finalizando jornada (automático)' : '👤 Finalizando jornada (automatico)');
      console.log("esAutomatico", esAutomatico);

      setHoraInicio(null);
      setTiempoActual(0);
      setInicioEstadoActual(null);

      if (estadoAnterior !== 'desconectado') {
        console.log('Finalizando estado activo:', estadoAnterior);

        if (estadoAnterior === 'break') {
          const breakActual = registroHoy.breaks[registroHoy.breaks.length - 1];
          if (breakActual && !breakActual.fin) {
            breakActual.fin = horaSalidaActual;
          }
        }

        if (estadoAnterior === 'almuerzo') {
          if (registroHoy.almuerzo && !registroHoy.almuerzo.fin) {
            setRegistroHoy(prev => ({
              ...prev,
              almuerzo: { ...prev.almuerzo, fin: horaSalidaActual }
            }));
          }
        }

        if (excedenteActual > 0) {
          setRegistroHoy(prev => ({
            ...prev,
            tiempoReponer: (prev?.tiempoReponer || 0) + excedenteActual,
          }));
        }
      }
      // El backend cierra el estado activo y crea 'cierrejornada' al llamar marcarSalida

      setEstadoActual('desconectado');
      
      const res = await marcarSalida(asesorId, { 
        tipo_cierre: esAutomatico ? 'automatico' : 'manual',
        hora_salida: finISOString
      });




      setRegistroHoy(prev => ({
        ...prev,
        horaSalida: horaSalidaActual,
      }));

      setPuedeFinalizar(false);
      setJornadaCerrada(true);
      setExcedenteActual(0);
      setAlertado(false);

      enviarEstadoWebSocket('desconectado', {
        ...res,
        evento: 'end_shift',
        estadoAnterior,
        horaSalida: horaSalidaActual,
        fin: finISOString,
        automatico: esAutomatico
      });

      setTimeout(() => {
        verificarJornadaActual();
      }, 1500);
    } catch (err) {
      console.error('Error al marcar salida:', err);
      notifyError(`Error al marcar salida: ${err.response?.data?.detail || err.message}`);
    }
  };

  const cargarHistorialHoy = async () => {
    try {
      const hoyStr = new Date().toISOString().split('T')[0];
      const data = await getHistorialAsesor(asesorId, hoyStr);
      setHistorialHoy(data?.transiciones || []);
    } catch (err) {
      console.error('Error al cargar historial de hoy:', err);
    }
  };

  const conectarWebSocket = () => {
    const explicit = import.meta.env.VITE_WS_URL;
    const fallback = 'wss://tu-fallback-si-aplica';
    const WS_URL = explicit || fallback;

    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      console.log('[WS] Ya hay un WebSocket activo, no se crea otro');
      return ws;
    }

    try {
      console.log('[WS] 🔄 Iniciando conexión a:', WS_URL);
      const websocket = new WebSocket(WS_URL);
      
      const connectionTimeout = setTimeout(() => {
        if (websocket.readyState !== WebSocket.OPEN) {
          console.log('[WS] ⏱️ Timeout de conexión, cerrando...');
          websocket.close();
        }
      }, 10000);

      websocket.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log('[WS] 🟢 Conectado exitosamente');
        
        reconnectAttemptsRef.current = 0;
        sessionStorage.removeItem('ws_reconnect_attempts');

        websocket.send(JSON.stringify({
          type: 'identify',
          userId: asesorId,
          nombre: user.nombre,
          cargo: user.cargo,
          area: user.area,
          timestamp: new Date().toISOString(),
        }));

        const estadoActualizado = estadoActualRef.current;
        if (estadoActualizado && estadoActualizado !== 'desconectado') {
          websocket.send(JSON.stringify({
            type: 'estado_cambio',
            userId: asesorId,
            nombre: user.nombre,
            cargo: user.cargo,
            area: user.area,
            estado: estadoActualizado,
            estado_slug: estadoActualizado,
            timestamp: new Date().toISOString(),
            origen: 'reconnect',
            horaEntrada: registroHoy.horaEntrada,
            jornadaActiva: registroHoy.horaEntrada && !registroHoy.horaSalida,
          }));
          console.log('[WS] ✅ Estado actual enviado:', estadoActualizado);
        }

        setRequiereReconexion(false);
        setMostrarModalInactividad(false);
        ultimaActividadRef.current = Date.now();
      };

      websocket.onclose = (evt) => {
        clearTimeout(connectionTimeout);
        console.log(
          `[WS] 🔴 Conexión cerrada -> code: ${evt.code}, reason: "${evt.reason}", clean: ${evt.wasClean}`
        );

        if (syncIntervalRef.current) {
          clearInterval(syncIntervalRef.current);
          syncIntervalRef.current = null;
        }
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        if (evt.code === CIERRE_INACTIVIDAD_CODE) {
          console.log('[WS] 🚫 Cierre por inactividad del usuario, esperando reconexión manual');
          setWs(null);
          wsRef.current = null;
          return;
        }

        setWs(null);
        wsRef.current = null;
        
        reconnectAttemptsRef.current++;
        const attempts = reconnectAttemptsRef.current;
        const delay = Math.min(3000 * Math.pow(2, attempts - 1), 30000);
        
        console.log(`[WS] 🔄 Reintentando conexión en ${delay/1000}s (intento ${attempts})...`);
        
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('[WS] Ejecutando reconexión automática...');
          conectarWebSocket();
        }, delay);
      };

      websocket.onerror = (err) => {
        console.error('[WS] ❌ Error:', err);
        notifyError('Error en la conexión WebSocket', { duration: 3000 });
      };

      setWs(websocket);
      wsRef.current = websocket;
      return websocket;
    } catch (err) {
      console.error('Error creando WebSocket:', err);
      notifyError('No se pudo conectar con el servidor en tiempo real');
      return null;
    }
  };

  useEffect(() => {
    const marcarActividad = () => {
      ultimaActividadRef.current = Date.now();

      if (mostrarModalInactividadRef.current && !requiereReconexionRef.current) {
        setMostrarModalInactividad(false);
      }
    };

    window.addEventListener('mousemove', marcarActividad);
    window.addEventListener('keydown', marcarActividad);
    window.addEventListener('click', marcarActividad);
    window.addEventListener('scroll', marcarActividad);
    window.addEventListener('touchstart', marcarActividad);

    return () => {
      window.removeEventListener('mousemove', marcarActividad);
      window.removeEventListener('keydown', marcarActividad);
      window.removeEventListener('click', marcarActividad);
      window.removeEventListener('scroll', marcarActividad);
      window.removeEventListener('touchstart', marcarActividad);
    };
  }, []);

  const MODO_PRUEBA = true;

  useEffect(() => {
    const MAX_MS = import.meta.env.VITE_INACTIVIDAD_MAX_S * 1000 
    const WARNING_MS = MAX_MS;
    const FALTAN_10S = WARNING_MS - 10000; //menos 10 segundos
    
    const timer = setInterval(() => {
      const estadoActualizado = estadoActualRef.current;
      if (estadoActualizado !== 'disponible') {
        return;
      }
      const diff = Date.now() - ultimaActividadRef.current;

      // Si ya se desconectó, detener contador
      if (requiereReconexionRef.current) return;

      // --- Mostrar contador 10 segundos antes ---
      if (diff >= FALTAN_10S && diff < WARNING_MS) {
        const segundosRestantes = Math.ceil((WARNING_MS - diff) / 1000);
        setContador(segundosRestantes);
      } else {
        setContador(null);
      }

      // --- Mostrar modal al llegar al WARNING ---
      if (diff >= WARNING_MS && diff < MAX_MS && !mostrarModalInactividadRef.current) {
        playBeep();
        notifyWarn(
          'Llevas un buen tiempo sin actividad. Si sigues inactivo te desconectaremos por seguridad.',
          { duration: 10000 }
        );
        setMostrarModalInactividad(true);
      }

      // --- Desconectar completamente al llegar al MAX_MS ---
      if (diff >= MAX_MS) {
        playBeep();

        try {
          notificarDesconexionWS('inactividad');
        } catch (e) {
          console.error('Error notificando desconexión por inactividad:', e);
        }

        const ahora = new Date().toISOString();
        transitionEstadoAsesor(asesorId, { estado: 'inactivo' });
        setEstadoActual('inactivo');
        setHoraInicio(ahora);
        setInicioEstadoActual(ahora);
        setTiempoActual(0);


        try {
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.close(CIERRE_INACTIVIDAD_CODE, 'Inactividad del usuario');
          }
        } catch (e) {
          console.error('Error cerrando WS por inactividad:', e);
        }

        setRequiereReconexion(true);
        setMostrarModalInactividad(true);
        notifyError(
          'Te hemos desconectado por inactividad. Debes reconectar para seguir usando el marcador.',
          { duration: 8000 }
        );
      }

    }, 1000); // <--- contador en tiempo real

    return () => clearInterval(timer);
  }, []);




  useEffect(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);

    pingIntervalRef.current = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        try {
          console.log('[WS] 🔄 Enviando ping...');
          ws.send(JSON.stringify({ type: 'ping' }));
        } catch (err) {
          console.error('[WS] Error enviando ping:', err);
        }
      }
    }, WS_PING_INTERVAL_MS);

    return () => clearInterval(pingIntervalRef.current);
  }, [ws]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        notificarDesconexionWS('cerrar_pestaña');
      } catch (e) {
        console.error('Error notificando desconexión al cerrar pestaña:', e);
      }

      try {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close(1000, 'Cerrar pestaña');
        }
      } catch (e) {
        console.error('Error cerrando WebSocket al cerrar pestaña:', e);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);



  // 3. useEffect para actualizar el tiempo restante en tiempo real
  useEffect(() => {
    if (estadoActual === 'desconectado' || estadoActual === 'jornada_activa' || !limiteActualSeg) {
      setTiempoRestanteEnVivo(null);
      setLimiteExcedido(false);
      return;
    }

    // Calcular inmediatamente al montar
    const restanteInicial = getTiempoRestante(estadoActual);
    setTiempoRestanteEnVivo(restanteInicial);
    setLimiteExcedido(restanteInicial < 0);

    const interval = setInterval(() => {
      const restante = getTiempoRestante(estadoActual);
      setTiempoRestanteEnVivo(restante);
      
      // 🔥 Detectar cuando se pasa del límite
      if (restante < 0 && !limiteExcedido) {
        setLimiteExcedido(true);
        playBeep(); // Reproducir sonido
        notifyWarn('Has superado el tiempo límite para este estado'); // Notificación
      } else if (restante >= 0 && limiteExcedido) {
        setLimiteExcedido(false);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [estadoActual, limiteActualSeg, estadosTiempo, tiempoActual, limiteExcedido]);

  const handleReconectarWS = () => {
    setRequiereReconexion(false);
    setMostrarModalInactividad(false);
    ultimaActividadRef.current = Date.now();
    reconnectAttemptsRef.current = 0;
    conectarWebSocket();
  };

  const colorActualDisplay = excedenteActual > 0 ? '#ef4444' : getEstadoColor();

  const hoy = new Date();
  const diaDeHoy = new Intl.DateTimeFormat('es-ES', {
    weekday: 'long',
    timeZone: 'America/Bogota',
  }).format(hoy);

  const mostrarBotonIngreso = 
    estadoActual === 'desconectado' && 
    puedeIniciar && 
    !registroHoy.horaEntrada && 
    !jornadaCerrada && 
    horarioHoy;

  const mostrarBotonSalida =
    registroHoy.horaEntrada &&
    !registroHoy.horaSalida &&
    !jornadaCerrada &&
    (estadoActual !== 'desconectado');

  const jornadaActiva = registroHoy.horaEntrada && !registroHoy.horaSalida && !jornadaCerrada;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Marcador de Tiempo</h2>
          <p style={styles.subtitle}>
            {user?.nombre || 'Usuario'} - {user?.cargo || 'Cargo'}
          </p>
        </div>
        <div style={styles.conexionStatus}>
          <div style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: ws?.readyState === WebSocket.OPEN ? '#10b981' : '#ef4444',
            marginRight: 8
          }} />
          <span style={{ fontSize: 14, color: 'var(--text-secondary)', marginRight: 8, fontFamily: "'Poppins', sans-serif", }}>
            {ws?.readyState === WebSocket.OPEN
              ? 'Conectado'
              : (requiereReconexion ? 'Desconectado por inactividad' : 'Desconectado')}
          </span>

          {requiereReconexion && (
            <button
              style={{
                padding: '6px 12px',
                borderRadius: 999,
                border: 'none',
                background: '#82cc0e',
                color: '#fff',
                fontSize: 12,
                fontFamily: "'Poppins', sans-serif",
                cursor: 'pointer',
              }}
              onClick={handleReconectarWS}
            >
              Reconectar
            </button>
          )}
        </div>
      </div>

      {/* Botón Finalizar */}
      {mostrarBotonSalida && (
        <div style={{
            width: '100%',            
            display: 'flex',
            justifyContent: 'flex-end' 
          }}>
          <button
           style={{
              ...styles.btnFinalizar,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              marginTop: 20,
              padding: '10px 16px',
              fontSize: '12px',
              fontFamily: "'Poppins', sans-serif",
              fontWeight: '600',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              background:
                'linear-gradient(135deg, #f50000ff 0%, #fa0021ff 100%)',
              color: 'white',
              minWidth: '120px',
              transform: 'scale(1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
            }}
            onClick={() => handleMarcarSalida(false)}
          >
            <StopCircle size={28} />
            <span>Finalizar Jornada</span>
          </button>
        </div>
      )}

      <div style={styles.mainCard}>
        {/* Reloj principal */}
        <div style={{
          ...styles.relojPrincipal,
          background: `linear-gradient(135deg, ${colorActualDisplay}15, ${colorActualDisplay}05)`
        }}>
          <div style={{ ...styles.estadoBadge, background: colorActualDisplay }}>
            {getEstadoTexto()}
          </div>

          <div style={styles.relojDisplay}>
            <Clock size={48} style={{ color: colorActualDisplay, marginBottom: 16 }} />
            <div style={{
              fontSize: 72,
              fontWeight: 700,
              color: colorActualDisplay,
              fontFamily: "'Poppins', sans-serif",
              letterSpacing: 4
            }}>
              {formatearTiempo(tiempoActual)}
            </div>


            {limiteActualSeg != null && estadoActual !== 'desconectado' && estadoActual !== 'jornada_activa' && (
              <div style={{
                marginTop: 16,
                marginBottom: 8,
                padding: '10px 24px',
                background: limiteExcedido ? '#fee2e2' : styles.relojDisplay, 
                borderRadius: 10,
                border: `2px solid ${limiteExcedido ? '#ef4444' : colorActualDisplay}`, 
                display: 'inline-block',
                boxShadow: limiteExcedido ? '0 4px 12px rgba(239, 68, 68, 0.3)' : '0 2px 6px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                animation: limiteExcedido ? 'pulse 2s infinite' : 'none' 
              }}>
                <div style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: limiteExcedido ? '#991b1b' : '#666',
                  marginBottom: 4,
                  fontFamily: "'Poppins', sans-serif",
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {limiteExcedido ? 'TIEMPO EXCEDIDO' : 'TIEMPO RESTANTE LÍMITE'}
                </div>
                <div style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: limiteExcedido ? '#ef4444' : colorActualDisplay,
                  fontFamily: "'Poppins', sans-serif",
                  letterSpacing: '2px'
                }}>
                  {tiempoRestanteEnVivo !== null 
                    ? (tiempoRestanteEnVivo >= 0 
                        ? formatearTiempo(tiempoRestanteEnVivo)
                        : '+' + formatearTiempo(Math.abs(tiempoRestanteEnVivo)))
                    : '00:00:00'}
                </div>
                <div style={{
                  fontSize: 10,
                  color: limiteExcedido ? '#991b1b' : '#999',
                  marginTop: 4,
                  fontFamily: "'Poppins', sans-serif",
                }}>
                  Usado hoy: {Math.floor((estadosTiempo[estadoActual]?.total_segundos || 0) / 60) + Math.floor(tiempoActual / 60)} min
                  {limiteExcedido && ` / Límite: ${Math.floor(limiteActualSeg / 60)} min`}
                </div>
              </div>
            )}
            <p style={{ fontSize: 16, color: 'var(--text-secondary)', marginTop: 8, fontFamily: "'Poppins', sans-serif", }}>
              {limiteActualSeg != null
                ? (tiempoActual <= limiteActualSeg
                    ? `Límite: ${formatearTiempo(limiteActualSeg)}`
                    : `Excedente: +${formatearTiempo(excedenteActual)}`)
                : (estadoActual === 'jornada_activa' ? 'Selecciona un estado para comenzar'
                    : estadoActual === 'disponible' ? 'Tiempo trabajando'
                    : estadoActual === 'break' ? 'Tiempo en break'
                    : estadoActual === 'almuerzo' ? 'Tiempo de almuerzo'
                    : 'Fuera de servicio')}
            </p>
            {horarioHoy && (
              <p style={{ fontSize: 15, color: '#666', marginTop: 10, fontFamily: "'Poppins', sans-serif", }}>
                Horario de hoy {diaDeHoy}: {horarioHoy.hora_entrada?.slice(0, 5)} - {horarioHoy.hora_salida?.slice(0, 5)}
                {proximaFinalizacionAuto && (
                  <span style={{ display: 'block', fontSize: 12, color: '#9ca3af', marginTop: 4, fontFamily: "'Poppins', sans-serif", }}>
                    (Finalización automática: {proximaFinalizacionAuto.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })})
                  </span>
                )}
              </p>
            )}
            {mensajeHorario && (
              <p style={{ fontSize: 14, color: '#f59e0b', marginTop: 8, fontWeight: 500, fontFamily: "'Poppins', sans-serif", }}>
                {mensajeHorario}
              </p>
            )}
          </div>
        </div>

        {/* Botón Iniciar */}
        {mostrarBotonIngreso && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              style={{ 
                ...styles.btnIniciar,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                marginTop: 20,
                padding: '16px 32px',
                fontSize: '18px',
                fontFamily: "'Poppins', sans-serif",
                fontWeight: '600',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                background: 'linear-gradient(135deg, #82cc0e 0%, #82cc0e 100%)',
                color: 'white',
                minWidth: '220px',
                transform: 'scale(1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }}
              onClick={handleMarcarEntrada}
            >
              <PlayCircle size={28} />
              <span>Iniciar Jornada</span>
            </button>
          </div>
        )}
        
        {jornadaCerrada && (
          <div style={{
            marginTop: 12,
            padding: 12,
            background: '#e5e7eb',
            border: '1px solid #d1d5db',
            borderRadius: 8,
            fontSize: 13,
            fontFamily: "'Poppins', sans-serif",
            color: '#374151',
            textAlign: 'center'
          }}>
            Jornada finalizada. Los estados están desactivados hasta mañana.
          </div>
        )}

        {/* Mensaje cuando no puede iniciar/finalizar */}
        {!mostrarBotonIngreso && !mostrarBotonSalida && !jornadaCerrada && (
          <div style={{
            marginTop: 12,
            padding: 12,
            background: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: 8,
            fontSize: 13,
            fontFamily: "'Poppins', sans-serif",
            color: '#6b7280',
            textAlign: 'center'
          }}>
            {!horarioHoy 
              ? 'No hay horario asignado para hoy'
              : mensajeHorario || 'Fuera de horario laboral'}
          </div>
        )}

        {/* Mis estados (API) */}
        <div style={{ marginTop: 24 }}>
          <h4 style={{ margin: '0 0 12px 0', color: 'var(--text)' }}>Mis estados</h4>

          {cargandoEstados && <p style={{ color: 'var(--text-secondary)' }}>Cargando estados…</p>}
          {errorEstados && (
            <div style={{
              padding: 16,
              background: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: 8,
              color: '#991b1b',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <AlertCircle size={20} />
              <span>{errorEstados}</span>
            </div>
          )}

          {!cargandoEstados && !errorEstados && estadosDisponibles.length === 0 && (
            <p style={{ color: 'var(--text-secondary)' }}>No tienes estados asignados.</p>
          )}

          {!cargandoEstados && estadosDisponibles.length > 0 && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                {estadosDisponibles.map(est => {
                  const code = est.codigo || est.slug;
                  const Icon = iconoDe(code);
                  const color = est.color_hex || colorDe(code);
                  
                  if (code === 'ingreso' || code === 'salida') {
                    return null;
                  }

                  const configInactiva = est.activo_config === false || est.activo_catalogo === false;
                  const noHaIniciadoJornada = estadoActual === 'desconectado' && !registroHoy.horaEntrada;
                  
                  const disabled = 
                    configInactiva || 
                    noHaIniciadoJornada || 
                    jornadaCerrada || 
                    fueraDeHorario;

                  const esEstadoActual = estadoActual === code;
                  const disabledFinal = disabled || esEstadoActual;

                  let tooltip = est.nombre || labelDe(code);
                  if (esEstadoActual) {
                    tooltip = 'Estado actual (activo)';
                  } else if (configInactiva) {
                    tooltip = 'Estado inactivo en la configuración';
                  } else if (noHaIniciadoJornada) {
                    tooltip = 'Debes iniciar jornada primero';
                  } else if (jornadaCerrada) {
                    tooltip = 'La jornada ya fue finalizada';
                  } else if (fueraDeHorario) {
                    tooltip = 'Fuera de horario laboral';
                  }

                  return (
                    <button
                      key={est.id ?? code}
                      onClick={() => !disabledFinal && handleCambiarEstado(code)}
                      disabled={disabledFinal}
                      style={{
                        ...styles.btnControl,
                        padding: '14px 18px',
                        background: disabledFinal 
                          ? '#9ca3af' 
                          : `linear-gradient(135deg, ${color}, ${color})`,
                        opacity: disabledFinal ? 0.5 : 1,
                        cursor: disabledFinal ? 'not-allowed' : 'pointer',
                        border: esEstadoActual ? '2px solid rgba(255,255,255,.8)' : 'none',
                        boxShadow: esEstadoActual 
                          ? '0 0 20px rgba(255,255,255,.3)' 
                          : 'none'
                      }}
                      title={tooltip}
                    >
                      <Icon size={22} />
                      <span>{est.nombre || labelDe(code)}</span>
                    </button>
                  );
                })}
              </div>
              {!registroHoy.horaEntrada && !mostrarBotonIngreso && (
                <div style={{
                  marginTop: 12,
                  padding: 12,
                  background: '#fef3c7',
                  border: '1px solid #fcd34d',
                  borderRadius: 8,
                  fontSize: 13,
                  fontFamily: "'Poppins', sans-serif",
                  color: '#92400e',
                  textAlign: 'center'
                }}>
                  Espera a que llegue tu horario para iniciar jornada
                </div>
              )}
              
             
            </>
          )}
        </div>
      </div>
      

      {contador !== null && (
        <>
          {/* Elemento de audio que dura los 10 segundos completos */}
          <audio autoPlay loop volume="1.0">
            <source src="https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3" type="audio/mpeg" />
          </audio>
          
          <div
            style={{
              position: 'fixed',
              top: 80,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'repeating-linear-gradient(45deg, #DC2626, #DC2626 20px, #991B1B 20px, #991B1B 40px)',
              color: contador <= 3 ? '#ffffffff' : '#FFFFFF',
              padding: '14px 40px',
              borderRadius: 6,
              zIndex: 9999,
              fontWeight: '700',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.4)',
              border: '3px solid #7F1D1D',
              minWidth: '400px',
              textAlign: 'center',
              transition: 'color 0.5s ease',
              fontSize: '16px',
              letterSpacing: '0.5px'
            }}
          >
            Te desconectaremos en {contador} segundos...
          </div>
        </>
      )}
      
   
      {mostrarModalInactividad && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.62)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: '#ffffff',
              padding: 24,
              borderRadius: 12,
              maxWidth: 400,
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            }}
          >
            <h3 style={{ margin: '0 0 12px 0', fontSize: 18, fontFamily: "'Poppins', sans-serif", }}>Sesión inactiva</h3>
            <p style={{ fontSize: 14, color: '#4b5563', marginBottom: 20, fontFamily: "'Poppins', sans-serif", }}>
              Te hemos desconectado por inactividad. Para seguir usando el marcador, debes reconectar tu sesión en tiempo real.
            </p>

            <button
              onClick={handleReconectarWS}
              style={{
                padding: '10px 20px',
                borderRadius: 999,
                border: 'none',
                background: '#3baf1eff',
                color: '#fff',
                fontSize: 14,
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Reconectar ahora
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: { padding: 24, maxWidth: 900, margin: '0 auto', background: 'var(--bg)', minHeight: '100vh' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 },
  title: { margin: 0, fontSize: 28, fontWeight: 700, color: 'var(--text)' },
  subtitle: { margin: '4px 0 0 0', fontSize: 14, color: 'var(--text-secondary)' },
  conexionStatus: { display: 'flex', alignItems: 'center', padding: '8px 16px', background: 'var(--surface)', borderRadius: 20, border: '1px solid var(--border)' },
  mainCard: { background: 'var(--surface)', borderRadius: 16, padding: 32, boxShadow: '0 4px 12px var(--shadow)', marginBottom: 24 },
  relojPrincipal: { borderRadius: 12, padding: 40, textAlign: 'center', marginBottom: 32, position: 'relative' },
  estadoBadge: { display: 'inline-block', padding: '8px 24px', borderRadius: 20, color: 'white', fontSize: 14, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 24 },
  relojDisplay: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  controlesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 },
  btnControl: { padding: '16px 24px', border: 'none', borderRadius: 12, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, transition: 'all 0.2s', color: 'white' },
  btnIniciar: { background: 'linear-gradient(135deg, #10b981, #059669)', gridColumn: '1 / -1' },
  btnBreak: { background: 'linear-gradient(135deg, #f59e0b, #d97706)' },
  btnAlmuerzo: { background: 'linear-gradient(135deg, #3b82f6, #2563eb)' },
  btnDisponible: { background: 'linear-gradient(135deg, #10b981, #059669)', gridColumn: '1 / -1' },
  btnFinalizar: { background: 'linear-gradient(135deg, #ef4444, #dc2626)', gridColumn: '1 / -1' },
  autoFinishBanner: {},
};